// The spoken agent in the app: a bar that sits with the player while a
// conversation is on, and a button to start one. The engine is
// `voice/agent.tsx`; the Shell mounts the hook with the app's handlers, so
// "open the inbox" navigates and "pause" reaches the player, wherever you are.
import { Mic, PhoneOff } from 'lucide-react'
import { useAgentState } from '@/voice/agent'
import { useVoicePrefs } from '@/state/voicePrefs'
import { useAccess } from '@/state/access'
import { IconButton, cx } from './ui'

/**
 * The pill: floats above the tab bar and the player while a conversation is
 * on. What was heard in small type, what is being
 * said beneath, a meter, and hang up. Nothing while there is no conversation.
 */
export const VoiceBar = ({ className }: { className?: string }) => {
  const agent = useAgentState()
  const pushToTalk = useVoicePrefs((s) => s.pushToTalk)
  if (!agent.available || agent.status === 'disconnected') return null
  const level = agent.speaking ? agent.outLevel : agent.level
  const heard = agent.interim || agent.heard
  const line =
    agent.status === 'connecting'
      ? 'Connecting…'
      : agent.interim
        ? 'Listening…'
        : agent.said ||
          (agent.speaking
            ? 'Speaking…'
            : agent.turnPending || agent.heard
              ? 'Thinking…'
              : agent.muted && pushToTalk
                ? 'Hold the button to talk'
                : pushToTalk
                  ? 'Listening… let go when you are done'
                  : 'Listening… tap the button when you are done')
  return (
    <div
      className={cx(
        // the phone: a card the width of the screen above the tab bar, whose centre
        // button is the talk control; wider: the pill
        'fixed z-40 bg-panel border border-line shadow-[0_18px_50px_-12px_rgba(0,0,0,0.85),0_2px_8px_rgba(0,0,0,0.5)] flex items-center gap-3',
        'inset-x-3 bottom-[calc(var(--above-tabbar)+0.75rem)] rounded-[var(--radius-card)] px-4 py-3',
        'md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:bottom-4 md:max-w-[34rem] md:rounded-full md:pl-4 md:pr-1.5 md:py-1.5',
        className,
      )}
      role="status"
      data-testid="voice-bar"
    >
      <span
        className={cx(
          'w-2.5 h-2.5 rounded-full bg-accent shrink-0',
          agent.status === 'connected' && !agent.speaking && 'voice-listening',
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1 leading-snug">
        {heard && <span className="block text-xs text-faint line-clamp-2 md:truncate">“{heard}”</span>}
        <span className="block text-[15px] md:text-sm line-clamp-3 md:truncate">
          {line}
          {agent.error && <span className="text-amber-300/90"> · {agent.error}</span>}
        </span>
      </span>
      {agent.status === 'connected' && (
        <span className="inline-flex items-end gap-[3px] h-4 shrink-0" aria-hidden>
          {[0.2, 0.45, 0.7, 1].map((th) => (
            <span
              key={th}
              className={cx(
                'w-1 rounded-sm transition-colors',
                level >= th * 0.6 ? 'bg-accent' : 'bg-faint/40',
              )}
              style={{ height: `${6 + th * 10}px` }}
            />
          ))}
        </span>
      )}
      <IconButton label="Hang up" onClick={() => agent.stop()} data-testid="voice-hangup">
        <PhoneOff size={18} />
      </IconButton>
    </div>
  )
}

/** A small round mic for a toolbar: the same start and stop, no words. Hidden when the server has no agent. */
export const VoiceIconButton = ({ className }: { className?: string }) => {
  const agent = useAgentState()
  const member = useAccess((s) => s.member)
  if (!agent.available || member) return null
  const live = agent.status !== 'disconnected'
  return (
    <IconButton
      label={live ? 'Hang up' : 'Talk to the agent'}
      onClick={() => {
        if (live) agent.stop()
        else void agent.start().catch(() => {})
      }}
      className={cx(live && 'text-accent', className)}
      data-testid="voice-talk-icon"
    >
      <Mic size={16} />
    </IconButton>
  )
}

/** Start a conversation. Hidden when the server has no agent. */
export const VoiceButton = ({
  className,
  label = 'Talk',
  onStart,
}: {
  className?: string
  label?: string
  /** called as a conversation starts: a sheet holding the button closes so the bar shows */
  onStart?: () => void
}) => {
  const agent = useAgentState()
  if (!agent.available) return null
  const live = agent.status !== 'disconnected'
  return (
    <button
      type="button"
      onClick={() => {
        if (live) agent.stop()
        else {
          onStart?.()
          void agent.start().catch(() => {})
        }
      }}
      className={cx(
        'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition',
        live ? 'bg-accent text-on-accent' : 'bg-raised text-fg hover:brightness-110',
        className,
      )}
      aria-pressed={live}
      data-testid="voice-talk"
    >
      <Mic size={16} />
      {live ? 'Listening' : label}
    </button>
  )
}
